import type { AdType } from '@/modules/adv/types'
import type { GameAttributes, GameEvent, User } from '@/types'
import type { ApiAcknowledgement, ApiResult } from '@/utils/requests/api-result'
import type { FetchResult } from '@/utils/requests/types'

import { MessageAction, type OutcomingMessage } from '../main-frame/message-types'

export interface Order {
    amount: number
    gameHru: string
    gameTitle: string
    type: 'order'
    attributes: GameAttributes
    description?: string
    subtitle?: string
    photoUrl?: string
    externalId?: string
    bridgeId?: string
    placement?: AdType
}

export type DisableAdOrder = {
    amount: number
    type: 'disable-ad-order'
    durationInDays: 1 | 7 | 30
    externalId: string
    attributes: GameAttributes
    placement?: AdType
    isTopUp?: boolean
    subscription: 'basic-days'
}

export interface Topup {
    amount?: number
    type: 'topup'
}

interface Purchase {
    id: string
    purchaseToken: string
    status: string
    externalId: string
    bridgeId?: string
    [key: string]: unknown
}

export type PurchaseStatus = PaidStatus | FailedStatus

interface PaidStatus {
    status: 'PAID'
    orderId: string
    amount: number
    externalId?: string
}

interface FailedStatus {
    status: 'FAILED'
    error: string
}

export type ChargeResponse<T extends Order | DisableAdOrder | Topup> = T extends Order | DisableAdOrder
    ? Awaited<FetchResult<PurchaseStatus>>
    : User | null

export type PaymentModalOrderResponse<T = Order | DisableAdOrder | Topup | null> = T extends
    | Order
    | DisableAdOrder
    | Topup
    ? Promise<ChargeResponse<T>>
    : undefined

export type ChargeUserFunction = <T extends Order | DisableAdOrder | Topup | null>(
    value: T,
) => PaymentModalOrderResponse<T>

interface IgpParams {
    gameId: string
    orderId: string
    externalId: string
}

interface CreateInGamePaymentsOptions {
    gameId: string
    gameHru: string
    gameTitle: string
    chargeUser: ChargeUserFunction
    attributes: GameAttributes
    confirmDelivery?: (params: IgpParams) => Promise<ApiAcknowledgement>
    getPurchases?: (gameId: string) => Promise<ApiResult<Purchase[]>>
    consumePurchase?: (params: IgpParams) => Promise<ApiAcknowledgement>
}

const purchaseMessage = (res: Awaited<FetchResult<PurchaseStatus>>, event: GameEvent) => {
    let payload = res.data
    if (res.errors.length) {
        payload = {
            status: 'FAILED',
            error: res.errors.join(', '),
        }
    }

    return {
        type: 'in-game-payments',
        action: MessageAction.purchaseStatus,
        originalMessageEvent: event,
        payload,
    } as OutcomingMessage
}

export const createInGamePayments = ({
    gameId,
    gameHru,
    gameTitle,
    chargeUser,
    attributes,
    confirmDelivery,
    getPurchases,
    consumePurchase,
}: CreateInGamePaymentsOptions) => {
    const messageHandler = async (event: GameEvent) => {
        switch (event.data.action) {
            case MessageAction.requestPurchase: {
                try {
                    const purchaseData = await chargeUser<Order>({
                        type: 'order',
                        gameHru,
                        gameTitle,
                        attributes,
                        ...event.data.payload,
                    } as Order)
                    return purchaseMessage(purchaseData, event)
                } catch (e) {
                    console.error('Caught error during purchase request:', e)
                    return purchaseMessage(
                        {
                            errors: [e?.toString() as string],
                            data: null,
                            // @ts-expect-error fill later
                            originalResponse: null,
                        },
                        event,
                    )
                }
            }
            case MessageAction.requestTopup: {
                try {
                    const userInfo = await chargeUser({ type: 'topup', ...event.data.payload } as Topup)
                    return {
                        type: 'in-game-payments',
                        action: MessageAction.topupStatus,
                        originalMessageEvent: event as GameEvent,
                        payload: userInfo,
                    } as OutcomingMessage
                } catch (e) {
                    return purchaseMessage(
                        {
                            errors: [e?.toString() as string],
                            data: null,
                            // @ts-expect-error fill later
                            originalResponse: null,
                        },
                        event,
                    )
                }
            }
            case MessageAction.confirmDelivery: {
                if (confirmDelivery) {
                    const { orderId, externalId } = (event.data.payload ?? {}) as {
                        orderId: string
                        externalId: string
                    }
                    const result = await confirmDelivery({ gameId, orderId, externalId })

                    if (result.status === 'error') {
                        console.error('Caught error during confirmDelivery:', result.message)
                    }
                }

                return {
                    type: 'in-game-payments',
                    action: MessageAction.confirmDeliveryStatus,
                    originalMessageEvent: event,
                    payload: {},
                } as OutcomingMessage
            }
            case MessageAction.getPurchases: {
                const result = getPurchases ? await getPurchases(gameId) : { status: 'ok' as const, data: [] }
                if (result.status === 'error') {
                    console.error('Caught error during getPurchases:', result.message)
                }

                return {
                    type: 'in-game-payments',
                    action: MessageAction.purchasesResponse,
                    originalMessageEvent: event,
                    payload: { purchases: result.status === 'ok' ? result.data : [] },
                } as OutcomingMessage
            }
            case MessageAction.consumePurchase: {
                if (consumePurchase) {
                    const { orderId, externalId } = (event.data.payload ?? {}) as {
                        orderId: string
                        externalId: string
                    }

                    const result = await consumePurchase({ gameId, orderId, externalId })
                    if (result.status === 'error') {
                        console.error('Caught error during consumePurchase:', result.message)
                        return {
                            action: MessageAction.consumePurchaseStatus,
                            originalMessageEvent: event,
                            payload: { error: result.message },
                            type: 'in-game-payments',
                        } as OutcomingMessage
                    }
                }

                return {
                    type: 'in-game-payments',
                    action: MessageAction.consumePurchaseStatus,
                    originalMessageEvent: event,
                    payload: {},
                } as OutcomingMessage
            }
            default:
                return {
                    action: MessageAction.unknownMessageType,
                    originalMessageEvent: event as GameEvent,
                    payload: {
                        error: `Unknown message action "${event.data.action}"`,
                    },
                    type: 'error',
                } as OutcomingMessage
        }
    }

    return messageHandler
}

export type InGamePaymentsMessageHandler = (event: GameEvent) => Promise<OutcomingMessage>
